import React from "react";
import {
  formatActionLabel,
  formatCallTime,
  formatPhone,
  getActionDetailsLabel,
  renderActionCell,
  YesNoPill,
} from "./callCellRenderers";

export const CALLS_COLUMN_STORAGE_KEY = "twilanswer.dashboard.callColumns";

/** Multi-select: empty array / undefined = show all */
export const multiSelectFilterFn = (row, columnId, filterValue) => {
  if (!filterValue || !Array.isArray(filterValue) || filterValue.length === 0) {
    return true;
  }
  const value = row.getValue(columnId);
  return filterValue.includes(value);
};

/** Boolean Yes/No: filterValue is "Yes" | "No" | "" | undefined */
export const booleanFilterFn = (row, columnId, filterValue) => {
  if (!filterValue) return true;
  return row.getValue(columnId) === filterValue;
};

/** Text contains (case-insensitive) */
export const textSearchFilterFn = (row, columnId, filterValue) => {
  if (!filterValue || String(filterValue).trim() === "") return true;
  const value = String(row.getValue(columnId) ?? "").toLowerCase();
  return value.includes(String(filterValue).toLowerCase().trim());
};

/**
 * Column metadata + TanStack column definitions.
 * Order matches product matrix (1–17); details link is always last and non-hideable.
 */
export const COLUMN_META = [
  {
    id: "callTime",
    dbField: "startTime",
    label: "Call Time",
    filterType: "date",
    defaultVisible: true,
    defaultOrder: 1,
    accessorKey: "callTime",
  },
  {
    id: "toNumber",
    dbField: "toNumber",
    label: "To Number",
    filterType: "multi",
    defaultVisible: true,
    defaultOrder: 2,
    accessorKey: "toNumber",
  },
  {
    id: "callerNumber",
    dbField: "fromNumber",
    label: "Caller Number",
    filterType: "multi",
    defaultVisible: true,
    defaultOrder: 3,
    accessorKey: "callerNumber",
  },
  {
    id: "action",
    dbField: "action",
    label: "Action",
    filterType: "multi",
    defaultVisible: true,
    defaultOrder: 4,
    accessorKey: "action",
  },
  {
    id: "actionDetails",
    dbField: "action_details",
    label: "Action Details",
    filterType: "multi",
    defaultVisible: true,
    defaultOrder: 5,
    accessorKey: "actionDetails",
  },
  {
    id: "voicemail",
    dbField: "has_vm",
    label: "Voicemail",
    filterType: "boolean",
    defaultVisible: true,
    defaultOrder: 6,
    accessorKey: "voicemail",
  },
  {
    id: "callUser",
    dbField: "call_user",
    label: "Call User",
    filterType: "multi",
    defaultVisible: false,
    defaultOrder: 7,
    accessorKey: "callUser",
  },
  {
    id: "hasRecording",
    dbField: "has_recording",
    label: "Recording Enabled",
    filterType: "boolean",
    defaultVisible: false,
    defaultOrder: 8,
    accessorKey: "hasRecording",
  },
  {
    id: "hasTranscript",
    dbField: "has_transcript",
    label: "Transcription Enabled",
    filterType: "boolean",
    defaultVisible: false,
    defaultOrder: 9,
    accessorKey: "hasTranscript",
  },
  {
    id: "route",
    dbField: "route",
    label: "Route",
    filterType: "multi",
    defaultVisible: false,
    defaultOrder: 10,
    accessorKey: "routeName",
  },
  {
    id: "isAfterHours",
    dbField: "isAfterHours",
    label: "After Hours",
    filterType: "boolean",
    defaultVisible: false,
    defaultOrder: 11,
    accessorKey: "isAfterHours",
  },
  {
    id: "notification",
    dbField: "notification",
    label: "Notification",
    filterType: "multi",
    defaultVisible: false,
    defaultOrder: 12,
    accessorKey: "notification",
  },
  {
    id: "notificationDetails",
    dbField: "notificationDetails",
    label: "Notification Detail",
    filterType: "multi",
    defaultVisible: false,
    defaultOrder: 13,
    accessorKey: "notificationDetails",
  },
  {
    id: "callId",
    dbField: "callId",
    label: "Call ID",
    filterType: "search",
    defaultVisible: false,
    defaultOrder: 14,
    accessorKey: "callId",
  },
  {
    id: "callSid",
    dbField: "callSid",
    label: "Twilio Call SID",
    filterType: "search",
    defaultVisible: false,
    defaultOrder: 15,
    accessorKey: "callSid",
  },
  {
    id: "contactName",
    dbField: "contactName",
    label: "Contact Name",
    filterType: "search",
    defaultVisible: false,
    defaultOrder: 16,
    accessorKey: "contactName",
  },
  {
    id: "contactCompany",
    dbField: "contactCompany",
    label: "Contact Company",
    filterType: "search",
    defaultVisible: false,
    defaultOrder: 17,
    accessorKey: "contactCompany",
  },
];

export const DETAILS_COLUMN_ID = "details";

export const DEFAULT_COLUMN_ORDER = [
  ...COLUMN_META.map((c) => c.id),
  DETAILS_COLUMN_ID,
];

export const getDefaultVisibility = () => {
  const visibility = {};
  COLUMN_META.forEach((c) => {
    visibility[c.id] = c.defaultVisible;
  });
  visibility[DETAILS_COLUMN_ID] = true;
  return visibility;
};

export const loadColumnPrefs = () => {
  try {
    const raw = localStorage.getItem(CALLS_COLUMN_STORAGE_KEY);
    if (!raw) {
      return {
        columnVisibility: getDefaultVisibility(),
        columnOrder: DEFAULT_COLUMN_ORDER,
      };
    }
    const parsed = JSON.parse(raw);
    const knownIds = new Set(COLUMN_META.map((c) => c.id));
    const defaults = getDefaultVisibility();

    const columnVisibility = { ...defaults };
    if (parsed.columnVisibility && typeof parsed.columnVisibility === "object") {
      Object.keys(parsed.columnVisibility).forEach((id) => {
        if (knownIds.has(id) || id === DETAILS_COLUMN_ID) {
          columnVisibility[id] = parsed.columnVisibility[id];
        }
      });
    }
    // Merge any new columns added later
    knownIds.forEach((id) => {
      if (!(id in columnVisibility)) {
        const meta = COLUMN_META.find((c) => c.id === id);
        columnVisibility[id] = meta ? meta.defaultVisible : true;
      }
    });
    columnVisibility[DETAILS_COLUMN_ID] = true;

    let columnOrder = Array.isArray(parsed.columnOrder)
      ? parsed.columnOrder.filter(
          (id) => knownIds.has(id) || id === DETAILS_COLUMN_ID
        )
      : [];
    // Append any missing known columns in default order
    DEFAULT_COLUMN_ORDER.forEach((id) => {
      if (!columnOrder.includes(id)) columnOrder.push(id);
    });
    // Ensure details is last
    columnOrder = columnOrder.filter((id) => id !== DETAILS_COLUMN_ID);
    columnOrder.push(DETAILS_COLUMN_ID);

    return { columnVisibility, columnOrder };
  } catch {
    return {
      columnVisibility: getDefaultVisibility(),
      columnOrder: DEFAULT_COLUMN_ORDER,
    };
  }
};

export const saveColumnPrefs = (columnVisibility, columnOrder) => {
  try {
    localStorage.setItem(
      CALLS_COLUMN_STORAGE_KEY,
      JSON.stringify({ columnVisibility, columnOrder })
    );
  } catch {
    // ignore quota / private mode
  }
};

const filterFnForType = (filterType) => {
  if (filterType === "boolean") return booleanFilterFn;
  if (filterType === "search") return textSearchFilterFn;
  if (filterType === "multi") return multiSelectFilterFn;
  return undefined; // date handled outside table
};

export const buildCallColumns = () => {
  const dataColumns = COLUMN_META.map((meta) => {
    const base = {
      id: meta.id,
      accessorKey: meta.accessorKey,
      header: meta.label,
      enableHiding: true,
      enableSorting: true,
      filterFn: filterFnForType(meta.filterType),
      meta: {
        filterType: meta.filterType,
        label: meta.label,
        dbField: meta.dbField,
      },
    };

    switch (meta.id) {
      case "callTime":
        return {
          ...base,
          cell: ({ getValue }) => (
            <span className="text-sm text-gray-900 whitespace-nowrap">
              {formatCallTime(getValue())}
            </span>
          ),
          sortingFn: (a, b, id) => {
            const av = Number(a.getValue(id)) || 0;
            const bv = Number(b.getValue(id)) || 0;
            return av - bv;
          },
        };
      case "toNumber":
      case "callerNumber":
        return {
          ...base,
          cell: ({ getValue }) => (
            <span className="text-sm text-gray-900 whitespace-nowrap">
              {formatPhone(getValue())}
            </span>
          ),
        };
      case "action":
        return {
          ...base,
          cell: ({ getValue, row }) =>
            renderActionCell(getValue(), {
              voicemailDropped: row.original?.voicemailDropped,
            }),
          sortingFn: (a, b, id) =>
            formatActionLabel(a.getValue(id) || "").localeCompare(
              formatActionLabel(b.getValue(id) || "")
            ),
        };
      case "actionDetails":
        return {
          ...base,
          cell: ({ getValue }) => (
            <span className="text-sm text-gray-900">
              {getActionDetailsLabel(getValue())}
            </span>
          ),
        };
      case "voicemail":
      case "hasRecording":
      case "hasTranscript":
      case "isAfterHours":
        return {
          ...base,
          cell: ({ getValue }) => <YesNoPill value={getValue()} />,
        };
      default:
        return {
          ...base,
          cell: ({ getValue }) => (
            <span className="text-sm text-gray-900">
              {getValue() || "—"}
            </span>
          ),
        };
    }
  });

  dataColumns.push({
    id: DETAILS_COLUMN_ID,
    header: "",
    enableHiding: false,
    enableSorting: false,
    enableColumnFilter: false,
    size: 48,
    cell: () => null, // rendered specially in CallsTable for navigation
    meta: { isDetails: true },
  });

  return dataColumns;
};

/** Stable list — do not recreate per render (used as useMemo deps) */
export const FILTERABLE_COLUMNS = COLUMN_META.filter(
  (c) => c.filterType !== "date"
);

export const getFilterableColumns = () => FILTERABLE_COLUMNS;

export const labelForColumnId = (id) => {
  if (id === DETAILS_COLUMN_ID) return "Details";
  const meta = COLUMN_META.find((c) => c.id === id);
  return meta?.label || id;
};
